import { useLocation, useSearchParams } from "@solidjs/router";
import { useQuery } from "@tanstack/solid-query";
import {
  type PageDetailResponse,
  PageDetailResponseSchema,
} from "contracts/api/dashboard/page-detail.responses";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { healthState, PAGE_THRESHOLDS } from "contracts/report/health";
import { createMemo, For, Show } from "solid-js";
import { SCAN_SECTION_KEY } from "../../enums/scan";
import { KEYBOARD_SCOPE } from "../../enums/shell";
import { setScope } from "../../keyboard/manager";
import { api } from "../../store/api";
import { SeverityBadge, StatusBadge } from "../../ui/Badge";
import { healthLabel, healthVar } from "../../ui/health";
import { singleParam } from "../../ui/searchParam";
import { pageDisplayLabel } from "../pages/boundaries/page-label.parser";
import { metricLabel } from "../website/issueModel";
import { createWebsiteStore } from "../website/store";
import { subcriterionLabel } from "./contentFitnessDimensionModel";
import { clientIssueTitle } from "./issueText";
import { ContentFitnessRationale } from "./PageContentFitnessRationale";
import {
  ContentFitnessDimensions,
  ContentFitnessScore,
} from "./PageContentFitnessSummary";
import { PageTechnicalDetail } from "./PageTechnicalDetail";
import {
  issueDetailFromPageHref,
  pageDetailReturnContext,
} from "./pageDetailNavigation";
import { ScanBreadcrumbs, ScanRouteSurface } from "./ScanRouteChrome";
import { ScanShell } from "./ScanShell";
import { ScreenshotFigure } from "./ScreenshotFigure";

export function ScanPageDetail(props: { websiteId: string; pageId: string }) {
  setScope(KEYBOARD_SCOPE.LIST);
  const context = createWebsiteStore(props.websiteId);
  const detail = useQuery(() => ({
    queryKey: ["page-detail", props.websiteId, props.pageId],
    queryFn: async () =>
      PageDetailResponseSchema.parse(
        await api.pageDetail(props.websiteId, props.pageId),
      ),
  }));

  return (
    <ScanShell context={context} active={SCAN_SECTION_KEY.PAGES}>
      <ScanRouteSurface class="signal-page-detail-route">
        <Show
          when={detail.data}
          keyed
          fallback={<div class="loading">Loading scan page...</div>}
        >
          {(value) => (
            <DetailView
              value={value}
              websiteId={props.websiteId}
              site={context.data()?.site ?? null}
            />
          )}
        </Show>
      </ScanRouteSurface>
    </ScanShell>
  );
}

function DetailView(props: {
  value: PageDetailResponse;
  websiteId: string;
  site: OverviewResponse["site"] | null;
}) {
  const page = () => props.value.page;
  const [search] = useSearchParams();
  const location = useLocation();
  const returnContext = createMemo(() =>
    pageDetailReturnContext(props.websiteId, page().id, {
      source: singleParam(search.from),
      returnQuery: singleParam(search.return),
      dimensionKey: singleParam(search.dimension),
      dimensionOrigin: singleParam(search.dimensionFrom),
      issueId: singleParam(search.issue),
      issueReturnQuery: singleParam(search.origin),
    }),
  );

  return (
    <>
      <ScanBreadcrumbs
        items={[
          { label: "Signal Scan", href: `/w/${props.websiteId}/scan` },
          {
            label: returnContext().parentLabel,
            href: returnContext().href,
          },
          { label: pageDisplayLabel(page(), props.site) },
        ]}
      />
      <div class="scan-detail-head">
        <div class="scan-detail-title-row">
          <div>
            <span class="scan-kicker">Page analysis</span>
            <h1 class="scan-title">{pageDisplayLabel(page(), props.site)}</h1>
            <a
              class="scan-page-url mono"
              href={page().url}
              target="_blank"
              rel="noreferrer"
            >
              {page().url}
            </a>
          </div>
          <ContentFitnessScore contentFitness={props.value.contentFitness} />
        </div>
      </div>

      <ContentFitnessDimensions contentFitness={props.value.contentFitness} />

      <Show when={props.value.issues.length > 0}>
        <section class="scan-panel">
          <div class="scan-section-head">
            <div>
              <span class="scan-kicker">Linked issues</span>
              <h2>Findings connected to this page</h2>
            </div>
          </div>
          <div class="scan-issue-list">
            <For each={props.value.issues}>
              {(issue) => (
                <a
                  class="scan-issue-row scan-page-linked-issue"
                  href={issueDetailFromPageHref(
                    props.websiteId,
                    issue.id,
                    page().id,
                    location.search,
                  )}
                >
                  <div class="scan-page-linked-issue-copy">
                    <strong>{clientIssueTitle(issue)}</strong>
                    <small>
                      {metricLabel(issue.metricKey)}
                      {issue.primarySubcriterionKey
                        ? ` / ${subcriterionLabel(issue.primarySubcriterionKey)}`
                        : ""}
                    </small>
                  </div>
                  <div class="scan-page-linked-issue-meta">
                    <SeverityBadge severity={issue.effectiveSeverity} />
                    <StatusBadge status={issue.status} />
                  </div>
                  <span class="scan-page-linked-issue-action">View issue</span>
                </a>
              )}
            </For>
          </div>
        </section>
      </Show>

      <ContentFitnessRationale contentFitness={props.value.contentFitness} />

      <section class="scan-panel">
        <div class="scan-section-head">
          <div>
            <span class="scan-kicker">Supporting diagnostics</span>
            <h2>AI Reference readout</h2>
            <p class="scan-muted">
              This secondary Agent diagnostic summarizes owned-site AI Reference
              inputs. It is separate from and does not contribute to the Content
              Fitness score.
            </p>
          </div>
        </div>
        <AgentDiagnostic score={page().agentScore} />
      </section>

      <Show when={props.value.screenshots.length > 0}>
        <section class="scan-panel">
          <div class="scan-section-head">
            <div>
              <span class="scan-kicker">Page evidence</span>
              <h2>
                Screenshots from this scan ({props.value.screenshots.length})
              </h2>
            </div>
          </div>
          <div class="scan-screenshot-grid">
            <For each={props.value.screenshots}>
              {(shot) => (
                <ScreenshotFigure
                  page={page()}
                  shot={shot}
                  websiteId={props.websiteId}
                />
              )}
            </For>
          </div>
        </section>
      </Show>

      <PageTechnicalDetail value={props.value} />
    </>
  );
}

function AgentDiagnostic(props: { score: number | null }) {
  const state = () =>
    healthState(props.score, PAGE_THRESHOLDS.good, PAGE_THRESHOLDS.watch);
  return (
    <div class="scan-metric scan-agent-diagnostic">
      <span>Agent diagnostic</span>
      <strong class="mono" style={{ color: healthVar(state()) }}>
        {props.score ?? "--"}
      </strong>
      <small class={`sig-state st-${state()}`}>{healthLabel(state())}</small>
    </div>
  );
}
